import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { SortableTableHeaderLabel, sensemakerTableBodyCellInteractiveSx, sensemakerTableSx } from '@/shared/table'

import {
  allocatedInstrumentsBodyCellSx,
  allocatedInstrumentsHeaderCellSx,
  ALLOCATED_INSTRUMENTS_TABLE,
  ALLOCATED_INSTRUMENTS_RESPONSE_PROGRESS,
} from '../lib/editor-viewer-dashboard-layout'
import {
  formatResponsesLabel,
  instrumentStatusPill,
  responseProgressForInstrument,
  type AllocatedInstrumentRow,
} from '../lib/allocated-instruments-display'
import { AllocatedInstrumentRowActions } from './AllocatedInstrumentRowActions'

interface Props {
  rows: AllocatedInstrumentRow[]
  campaignSortDirection: 'asc' | 'desc' | null
  onToggleCampaignSort: () => void
  canEdit: boolean
}

function StatusPill({ instrument }: Readonly<{ instrument: AllocatedInstrumentRow['instrument'] }>) {
  const pill = instrumentStatusPill(instrument)
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: '10px',
        py: '4px',
        borderRadius: 9999,
        bgcolor: pill.bg,
        border: `1px solid ${pill.border}`,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '16px',
          color: pill.color,
          whiteSpace: 'nowrap',
        }}
      >
        {pill.label}
      </Typography>
    </Box>
  )
}

function ResponsesCell({ row }: Readonly<{ row: AllocatedInstrumentRow }>) {
  const progress = responseProgressForInstrument(row.instrument)

  if (!progress.showBar) {
    return (
      <Typography sx={{ fontSize: 14, color: '#64748b' }}>—</Typography>
    )
  }

  return (
    <Stack spacing={0.75} sx={{ minWidth: 140, maxWidth: 280 }}>
      <LinearProgress
        variant="determinate"
        value={progress.percent}
        aria-label={formatResponsesLabel(progress.count, progress.target)}
        sx={{
          height: ALLOCATED_INSTRUMENTS_RESPONSE_PROGRESS.barHeight,
          borderRadius: ALLOCATED_INSTRUMENTS_RESPONSE_PROGRESS.barRadius,
          bgcolor: ALLOCATED_INSTRUMENTS_RESPONSE_PROGRESS.trackBg,
          overflow: 'hidden',
          ...(progress.barShadow ? { boxShadow: progress.barShadow } : {}),
          '& .MuiLinearProgress-bar': {
            borderRadius: ALLOCATED_INSTRUMENTS_RESPONSE_PROGRESS.barRadius,
            bgcolor: progress.barColor,
          },
        }}
      />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '16px',
          letterSpacing: '0.12px',
          color: progress.labelColor,
        }}
      >
        {formatResponsesLabel(progress.count, progress.target)}
      </Typography>
    </Stack>
  )
}

export function AllocatedInstrumentsTable({
  rows,
  campaignSortDirection,
  onToggleCampaignSort,
  canEdit,
}: Readonly<Props>) {
  return (
    <Table
      size="small"
      data-testid="ev-dashboard-instruments-table"
      sx={sensemakerTableSx}
    >
      <colgroup>
        <col style={{ width: ALLOCATED_INSTRUMENTS_TABLE.colNameWidth }} />
        <col style={{ width: ALLOCATED_INSTRUMENTS_TABLE.colStatusWidth }} />
        <col style={{ width: ALLOCATED_INSTRUMENTS_TABLE.colResponsesWidth }} />
        <col style={{ width: ALLOCATED_INSTRUMENTS_TABLE.colActionsWidth }} />
      </colgroup>
      <TableHead>
        <TableRow>
          <TableCell
            component="th"
            scope="col"
            sx={{
              ...allocatedInstrumentsHeaderCellSx,
              width: ALLOCATED_INSTRUMENTS_TABLE.colNameWidth,
              borderTopLeftRadius: ALLOCATED_INSTRUMENTS_TABLE.cardRadius,
            }}
          >
            <SortableTableHeaderLabel
              label="Instrument Name"
              sortDirection={campaignSortDirection}
              testId="ev-dashboard-instruments-campaign-sort"
              onToggle={onToggleCampaignSort}
            />
          </TableCell>
          <TableCell
            component="th"
            scope="col"
            sx={{
              ...allocatedInstrumentsHeaderCellSx,
              width: ALLOCATED_INSTRUMENTS_TABLE.colStatusWidth,
            }}
          >
            Status
          </TableCell>
          <TableCell
            component="th"
            scope="col"
            sx={{
              ...allocatedInstrumentsHeaderCellSx,
              width: ALLOCATED_INSTRUMENTS_TABLE.colResponsesWidth,
            }}
          >
            Responses
          </TableCell>
          <TableCell
            component="th"
            scope="col"
            sx={{
              ...allocatedInstrumentsHeaderCellSx,
              width: ALLOCATED_INSTRUMENTS_TABLE.colActionsWidth,
              minWidth: ALLOCATED_INSTRUMENTS_TABLE.colActionsMinWidth,
              borderTopRightRadius: ALLOCATED_INSTRUMENTS_TABLE.cardRadius,
            }}
          >
            Actions
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.instrument.id}
            data-testid={`ev-dashboard-instrument-row-${row.instrument.id}`}
          >
            <TableCell sx={allocatedInstrumentsBodyCellSx}>
              <Tooltip title={row.instrument.name} enterDelay={400}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: '20px',
                      color: ALLOCATED_INSTRUMENTS_TABLE.nameColor,
                    }}
                  >
                    {row.instrument.name}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 12,
                      lineHeight: '16px',
                      color: ALLOCATED_INSTRUMENTS_TABLE.campaignSubtitleColor,
                      mt: 0.25,
                    }}
                  >
                    {row.campaignName}
                  </Typography>
                </Box>
              </Tooltip>
            </TableCell>
            <TableCell sx={sensemakerTableBodyCellInteractiveSx}>
              <StatusPill instrument={row.instrument} />
            </TableCell>
            <TableCell sx={sensemakerTableBodyCellInteractiveSx}>
              <ResponsesCell row={row} />
            </TableCell>
            <TableCell sx={sensemakerTableBodyCellInteractiveSx}>
              <AllocatedInstrumentRowActions
                instrument={row.instrument}
                campaignId={row.campaignId}
                canEdit={canEdit}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
